import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Button, Row } from 'reactstrap'
import AutoTable from 'components/AutoTable'
import { IsRosteredBadge } from 'components/IsRosteredBadge/IsRosteredBadge'
import UserActions from 'components/UserActions'
import { getSitesApplicableRoles } from '../../lib/util'
import { ManualBadge } from 'components/ManualBadge/ManualBadge'
import AddUserModal from 'components/AddUserModal'
import {
  AddUserButton,
  ButtonsContainer,
  InviteAndRemindContainer,
  UserSummary,
  UserGridStatusColContainer,
  UserCount,
  UserCountPrefix,
} from './component.styles'
import SendInvitesDialog from '../SendInvitesDialog'

const SiteUsers = ({
  site,
  siteUsers,
  handleCancelUserAccess,
  handleGrantNewRole,
  addBulkUsersResponse,
  error,
  isBulkOperationCompleted,
  handleOpenAddUsersModal,
  handleCloseAddUsersModal,
  isAddUserModalOpen,
  remindSiteUser,
  remindSiteUsers,
  handleSubmitAddUsers,
  selectedSite,
}) => {
  const handleCloseSendInvitesModal = () => setInvitesModalOpen(false)
  const [sendInvitesModalOpen, setInvitesModalOpen] = useState(false)
  const [userMetaInfo, setUserMetaInfo] = useState({ all: 0, pending: 0 })

  useEffect(() => {
    createUserMetaData()
  }, [siteUsers])
  const activeUsers = siteUsers.filter(user => user.isActive === true)
  const createUserMetaData = () => {
    if (!siteUsers) {
      return
    }

    const activeUsers = siteUsers.filter(user => user.isActive === true)
    const newUserCounts = activeUsers.reduce(
      (counts, user) => {
        // currently we only allow one role per user
        const role = user.roles[0]

        if (role === 'Admin') {
          counts.admin = (counts.admin || 0) + 1
        }
        if (role === 'Leader Team') {
          counts.leaderTeam = (counts.leaderTeam || 0) + 1
        }
        if (role === 'Teacher') {
          counts.teacher = (counts.teacher || 0) + 1
        }
        if (!user.userId) {
          counts.pending = (counts.pending || 0) + 1
        }

        return counts
      },
      {
        all: activeUsers.length,
        admin: 0,
        leaderTeam: 0,
        teacher: 0,
        pending: 0,
      },
    )
    const filteredRoles = Object.entries(newUserCounts).filter(
      ([key]) => newUserCounts[key] > 0,
    )
    const filteredObject = Object.fromEntries(filteredRoles)
    setUserMetaInfo(filteredObject)
  }

  const formattUserGroup = siteUsersGroup => {
    const formattedUserGroup = []
    const existingRoles = [...new Set(activeUsers.map(user => user.roles[0]))]
    const sortedUserRoles = existingRoles.sort()

    for (const role of sortedUserRoles) {
      const users = activeUsers.filter(
        user => user.status === siteUsersGroup && user.roles[0] === role,
      )

      if (users.length) {
        formattedUserGroup.push(
          `${users.length} ${role}${
            role !== 'Leader Team' && role !== 'Staff' && users.length > 1
              ? 's'
              : ''
          }`,
        )
      }
    }
    return formattedUserGroup.join(', ')
  }
  const activeUserString = formattUserGroup('Active')
  const invitedUserString = formattUserGroup('Invited')

  const handleSendInvitesConfirm = ({ all, pending, ...roles }) => {
    const siteId = siteUsers[0].siteId
    const sendInvObj = { pendingUser: !!pending }

    if (!all && !pending && Object.values(roles).every(role => !role)) {
      setInvitesModalOpen(false)
    }

    if (all || pending) {
      remindSiteUsers(siteId, sendInvObj)
      setInvitesModalOpen(false)
      return
    }

    const sendToRoles = Object.entries(roles)
      .filter(([role, value]) => value && role !== 'all' && role !== 'pending')
      .map(([role]) =>
        role === 'leaderTeam'
          ? 'Leader Team'
          : `${role.charAt(0).toUpperCase()}${role.slice(1)}`,
      )

    if (sendToRoles.length > 0) {
      sendInvObj.roles = sendToRoles
    }
    remindSiteUsers(siteId, sendInvObj)
    setInvitesModalOpen(false)
  }

  const parseSiteUser = row => ({
    firstName: row.firstName,
    id: row.id,
    inviteEmail: row.inviteEmail,
    isActive: row.isActive,
    isRostered: row.isRostered,
    lastName: row.lastName,
    lastLoggedInAt: row.lastLoggedInAt,
    status: row.status,
    siteId: row.siteId,
    roles: row.roles,
    userId: row.userId,
  })

  const columns = [
    {
      title: 'Name',
      fieldValue: row => {
        const name = `${row.firstName || ''} ${row.lastName || ''}`
        const hasName = /\S/.test(name)
        return hasName ? name : ''
      },
    },
    {
      title: 'Email',
      fieldValue: row => {
        const userLink =
          !!row.inviteEmail && !!row.userId
            ? `/css-admin/users/${row.inviteEmail}/${row.userId}`
            : ''
        return userLink ? (
          <Link to={userLink}>{row.inviteEmail}</Link>
        ) : (
          row.inviteEmail
        )
      },
      sortValue: 'inviteEmail',
      filterValue: 'inviteEmail',
    },
    {
      title: 'Role',
      fieldValue: 'roles',
    },
    {
      title: 'Account Status',
      fieldValue: row => (
        <UserGridStatusColContainer>
          <span>{row.status}</span>
          <ManualBadge isRostered={row.isActive} />
          <IsRosteredBadge isRostered={row.isRostered} />
        </UserGridStatusColContainer>
      ),
      sortValue: 'status',
    },
    {
      title: 'Last Logged In',
      fieldValue: row => {
        return row.lastLoggedInAt
          ? new Date(row.lastLoggedInAt).toLocaleDateString('en-us', {
              weekday: 'long',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : ''
      },
      sortValue: 'lastLoggedInAt',
    },
    {
      title: '',
      sort: false,
      fieldValue: row => {
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <UserActions
              applicableRoles={getSitesApplicableRoles(site)}
              handleCancelUserAccess={handleCancelUserAccess}
              handleGrantNewRole={handleGrantNewRole}
              remindSiteUser={remindSiteUser}
              user={parseSiteUser(row)}
            />
          </div>
        )
      },
      cellClassName: 'text-center',
    },
  ]

  return (
    <React.Fragment>
      <InviteAndRemindContainer>
        <UserSummary>
          {activeUsers.length > 0 && (
            <UserCount>
              <UserCountPrefix>
                {activeUsers.length} active user
                {activeUsers.length > 1 ? 's ' : ''}
              </UserCountPrefix>
              &nbsp; ({activeUserString})
            </UserCount>
          )}
          {userMetaInfo.pending > 0 && (
            <UserCount>
              <UserCountPrefix>
                {userMetaInfo.pending} user
                {userMetaInfo.pending > 1 ? 's ' : ' '}
                without accounts
              </UserCountPrefix>
              &nbsp; ({invitedUserString})
            </UserCount>
          )}
        </UserSummary>
        <ButtonsContainer>
          <Button
            color="primary"
            onClick={() => setInvitesModalOpen(true)}
            size="sm"
          >
            <i className="fa fa-envelope" />
            &nbsp; Send Invites
          </Button>

          <AddUserButton
            color="primary"
            onClick={handleOpenAddUsersModal}
            size="sm"
          >
            <i className="fa fa-user-plus" />
            &nbsp;Add Users
          </AddUserButton>
        </ButtonsContainer>
      </InviteAndRemindContainer>
      <Row>
        <AutoTable
          borderless
          columns={columns}
          filter
          headThemeLight
          rows={siteUsers}
          sort
        />
      </Row>
      <SendInvitesDialog
        isOpen={sendInvitesModalOpen}
        onCancel={handleCloseSendInvitesModal}
        onConfirm={handleSendInvitesConfirm}
        userMetaInfo={userMetaInfo}
      />
      <AddUserModal
        addBulkUsersResponse={addBulkUsersResponse}
        isBulkOperationCompleted={isBulkOperationCompleted}
        closeModal={handleCloseAddUsersModal}
        error={error}
        isModalOpen={isAddUserModalOpen}
        handleSubmitAddUsers={handleSubmitAddUsers}
        selectedSite={selectedSite}
        handleOpenAddUsersModal={handleOpenAddUsersModal}
      />
    </React.Fragment>
  )
}

SiteUsers.propTypes = {
  handleCancelUserAccess: PropTypes.func.isRequired,
  handleGrantNewRole: PropTypes.func.isRequired,
  remindSiteUser: PropTypes.func.isRequired,
  remindSiteUsers: PropTypes.func.isRequired,
  site: PropTypes.object,
  siteUsers: PropTypes.array,
}

export default SiteUsers



// WEBPACK FOOTER //
// ./src/components/SiteUsers/component.js